import React, { useState, useEffect } from 'react';

const Attendance = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Theme detection
  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.body.getAttribute('data-theme') === 'dark' ||
        document.body.classList.contains('dark-mode') ||
        window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(isDark);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] });

    return () => observer.disconnect();
  }, []);

  const themeColors = {
    background: isDarkMode ? '#1a1a2e' : '#f5f7fa',
    cardBg: isDarkMode ? '#16213e' : '#ffffff',
    textPrimary: isDarkMode ? '#e8e8e8' : '#1a1a2a',
    textSecondary: isDarkMode ? '#a0a0a0' : '#666666',
    borderColor: isDarkMode ? '#2a2a4a' : '#e0e0e0',
    warningBg: isDarkMode ? '#3d2914' : '#fff3cd',
    warningBorder: isDarkMode ? '#8b5a2b' : '#ffc107',
    warningText: isDarkMode ? '#ffc107' : '#856404',
  };

  return (
    <div style={{ padding: '20px', background: themeColors.background, minHeight: '100vh' }}>
      <div className="page-header" style={{ marginBottom: '30px' }}>
        <h1 style={{ color: themeColors.textPrimary, margin: 0 }}>📋 Attendance Tracking</h1>
      </div>

      {/* Feature Not Available Card */}
      <div style={{
        background: themeColors.cardBg,
        borderRadius: '15px',
        padding: '40px',
        textAlign: 'center',
        boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
        border: `1px solid ${themeColors.borderColor}`,
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        {/* QR Code Icon */}
        <div style={{
          fontSize: '80px',
          marginBottom: '20px',
          opacity: 0.6
        }}>
          📱
        </div>

        {/* Warning Message */}
        <div style={{
          background: themeColors.warningBg,
          border: `2px solid ${themeColors.warningBorder}`,
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <h3 style={{
            color: themeColors.warningText,
            margin: '0 0 10px 0',
            fontSize: '18px'
          }}>
            ⚠️ QR Code Punching Device Not Installed
          </h3>
          <p style={{
            color: themeColors.warningText,
            margin: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            The QR Code attendance punching device is not installed in your GYM.
          </p>
        </div>

        {/* Feature Description */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            color: themeColors.textPrimary,
            margin: '0 0 15px 0',
            fontSize: '22px'
          }}>
            Attendance Tracking Feature
          </h2>
          <p style={{
            color: themeColors.textSecondary,
            margin: 0,
            fontSize: '15px',
            lineHeight: '1.7'
          }}>
            Once the QR Code punching device is available and configured, you will be able to:
          </p>
        </div>

        {/* Feature List */}
        <div style={{
          textAlign: 'left',
          background: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fa',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            color: themeColors.textSecondary
          }}>
            <li style={{ marginBottom: '12px', fontSize: '14px' }}>
              <strong style={{ color: themeColors.textPrimary }}>👥 Member Attendance</strong> - Track daily check-ins and check-outs for all gym members
            </li>
            <li style={{ marginBottom: '12px', fontSize: '14px' }}>
              <strong style={{ color: themeColors.textPrimary }}>👨‍💼 Staff Attendance</strong> - Monitor staff working hours and shifts
            </li>
            <li style={{ marginBottom: '12px', fontSize: '14px' }}>
              <strong style={{ color: themeColors.textPrimary }}>📊 Attendance Reports</strong> - Generate detailed attendance reports and analytics
            </li>
            <li style={{ marginBottom: '12px', fontSize: '14px' }}>
              <strong style={{ color: themeColors.textPrimary }}>📱 QR Code Scanning</strong> - Quick and contactless attendance marking
            </li>
            <li style={{ fontSize: '14px' }}>
              <strong style={{ color: themeColors.textPrimary }}>🔔 Real-time Notifications</strong> - Get alerts for attendance anomalies
            </li>
          </ul>
        </div>

        {/* Contact Support */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '10px',
          padding: '25px',
          color: 'white'
        }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>
            🎧 Enable This Feature
          </h3>
          <p style={{ margin: '0 0 20px 0', fontSize: '14px', opacity: 0.9 }}>
            Kindly contact our support team to enable the QR Code attendance tracking feature for your gym.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="mailto:support@gymkhata.com?subject=Enable%20Attendance%20Tracking%20Feature"
              style={{
                background: 'white',
                color: '#667eea',
                padding: '12px 25px',
                borderRadius: '25px',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📧 Email Support
            </a>
            <a
              href="/support"
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '12px 25px',
                borderRadius: '25px',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                border: '2px solid white'
              }}
            >
              💬 Chat Support
            </a>
          </div>
        </div>

        {/* Support Email */}
        <p style={{
          marginTop: '20px',
          color: themeColors.textSecondary,
          fontSize: '13px'
        }}>
          Email: <a href="mailto:support@gymkhata.com" style={{ color: '#667eea' }}>support@gymkhata.com</a>
        </p>
      </div>
    </div>
  );
};

export default Attendance;
